import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import {
  saveArtSettingsCommon,
  saveUserEdit,
} from '../../../../../../packages/common/api';
import FormField from '../../../../../../packages/common/shared-ui/src/components/FormField/FormField';
import { saveFormat } from '../../../../../../packages/common/utils/fieldHelper';
import { formValidation } from '../../../../../../packages/common/utils/formValidation';
import { useTranslation } from '../../../../../../packages/common/utils/getLocalString';
import { EditPageContext } from '../../EditPageContext';
import UserPageMainInfo from '../UserPageMainInfo';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));

const UserPageCommonTab = forwardRef((props, ref) => {
  const { fields, state, ...otherProps } = props;
  const PageState = { VIEW: 'view', EDIT: 'edit' };
  const getLocalString = useTranslation();
  const [allFields, setAllFields] = useState([]);
  const [errors, setErrors] = useState([]);
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fields) {
      setAllFields(fields);
    }
  }, [fields]);

  const handleFormField = useCallback(
    (data, field) => {
      const pos = allFields.map(e => e.fieldId).indexOf(field.fieldId);
      const fieldValue = {
        ...field,
        ...data,
      };
      const newFields = [
        ...allFields.slice(0, pos),
        fieldValue,
        ...allFields.slice(pos + 1),
      ];
      setAllFields(newFields);
    },
    [allFields],
  );

  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  const handleValidation = useCallback(() => {
    const validationResult = formValidation(allFields);
    setAllFields(validationResult.fields);
    setErrors(validationResult.errors);
    return validationResult.error;
  }, [allFields]);

  useImperativeHandle(ref, () => {
    return {
      async save() {
        const validation = handleValidation();
        if (validation) {
          return;
        }
        setLoading(true);
        let changedField;
        let changedFieldsStandard;
        let changedFieldsCustom;
        let resultStandardArray;
        let resultCustomsArray;
        changedField = allFields.filter(field => field.changed);
        changedFieldsStandard = changedField.filter(field => !field.custom);
        changedFieldsCustom = changedField.filter(field => field.custom);
        resultStandardArray = [...changedFieldsStandard];
        resultCustomsArray = [...changedFieldsCustom];
        const fields = saveFormat({
          standards: resultStandardArray,
          customs: resultCustomsArray,
        });
        let data = {
          ...fields,
        };
        try {
          const response = await saveUserEdit(params.id, data);
          if (response) {
            setLoading(false);
            window.open(`/administration/users/${params.id}`, '_self');
          }
        } catch (error) {
          setLoading(false);
          handleError(error.response);
        }
      },
    };
  });

  return (
    <Grid
      container
      sx={{
        marginTop: '20px',
      }}
    >
      <Grid
        sx={{
          width: '50%',
        }}
      >
        <StyledBlockWrapper
          sx={{
            position: 'relative',
          }}
        >
          {loading && (
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                zIndex: 99,
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <CircularProgress color='secondary' />
            </Grid>
          )}
          {errors.length > 0 && (
            <Box>
              {errors.map((error, index) => (
                <Typography
                  key={index}
                  variant='h3'
                  sx={{
                    color: '#E03737',
                  }}
                >
                  {getLocalString('errors', error.message)}
                </Typography>
              ))}
            </Box>
          )}
          <Typography
            variant='h3'
            sx={{
              p: 0,
              color: '#082253',
              fontWeight: '600',
            }}
          >
            {getLocalString('AdminUsers', 'labels', 'information')}
          </Typography>
          {state === PageState.VIEW && (
            <UserPageMainInfo fields={fields}></UserPageMainInfo>
          )}
          {state === PageState.EDIT && (
            <Grid container direction={'column'}>
              {fields.length > 0 &&
                allFields.map((field, index) => (
                  <FormField
                    key={index}
                    field={field}
                    fieldEvent={handleFormField}
                  ></FormField>
                ))}
            </Grid>
          )}
        </StyledBlockWrapper>
      </Grid>
    </Grid>
  );
});

export default UserPageCommonTab;
